import React, { useEffect, useRef, useState, useCallback } from "react";
import { Avatar } from "@/components/Avatar";
import { Mic, MicOff, Video, VideoOff, PhoneOff } from "lucide-react";

// Free public STUN servers (sufficient for peer-to-peer within-NAT in most cases).
// For users behind strict symmetric NATs, a TURN server would be needed later.
const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

/**
 * Props:
 * - roomId: string
 * - me: current user { id, username, avatar_gradient }
 * - wsRef: ref to shared WebSocket (from ChatRoom)
 * - registerHandler(fn): install a listener for video-related WS messages; returns unregister
 * - members: members_detail array (so we can show display info for remote peers)
 * - onLeave(): called when the user ends the call
 */
export default function VideoCall({ roomId, me, wsRef, registerHandler, members, onLeave }) {
  const [localStream, setLocalStream] = useState(null);
  const [remotes, setRemotes] = useState({}); // user_id -> { stream, username, avatar_gradient }
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [error, setError] = useState("");

  const pcsRef = useRef({}); // user_id -> RTCPeerConnection
  const localStreamRef = useRef(null);
  const joinedRef = useRef(false);
  const meRef = useRef(me);
  meRef.current = me;

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, [wsRef]);

  // Create RTCPeerConnection for a given peer user_id
  const createPeer = useCallback((peerId, peerInfo) => {
    if (pcsRef.current[peerId]) return pcsRef.current[peerId];
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    // Attach our local tracks
    const localS = localStreamRef.current;
    if (localS) localS.getTracks().forEach((t) => pc.addTrack(t, localS));

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        send({ type: "rtc-ice", to_user_id: peerId, payload: e.candidate });
      }
    };

    pc.ontrack = (e) => {
      const [stream] = e.streams;
      setRemotes((prev) => ({
        ...prev,
        [peerId]: {
          stream,
          username: peerInfo?.username || prev[peerId]?.username || "…",
          avatar_gradient: peerInfo?.avatar_gradient || prev[peerId]?.avatar_gradient,
        },
      }));
    };

    pc.onconnectionstatechange = () => {
      if (["failed", "closed", "disconnected"].includes(pc.connectionState)) {
        cleanupPeer(peerId);
      }
    };

    pcsRef.current[peerId] = pc;
    return pc;
  }, [send]);

  const cleanupPeer = useCallback((peerId) => {
    const pc = pcsRef.current[peerId];
    if (pc) {
      try { pc.close(); } catch {}
      delete pcsRef.current[peerId];
    }
    setRemotes((prev) => {
      if (!prev[peerId]) return prev;
      const n = { ...prev };
      delete n[peerId];
      return n;
    });
  }, []);

  // Main effect: get media, announce video-join, install signaling handler
  useEffect(() => {
    let active = true;

    (async () => {
      // Mobile-first media constraints: prefer front camera, moderate resolution
      // (saves bandwidth on cellular), and request echo cancellation + noise suppression.
      const baseConstraints = {
        video: {
          facingMode: "user",
          width: { ideal: 640, max: 1280 },
          height: { ideal: 480, max: 720 },
          frameRate: { ideal: 24, max: 30 },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      };
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(baseConstraints);
      } catch (err) {
        // Fallback: some older Android browsers reject advanced constraints
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        } catch (err2) {
          if (err2?.name === "NotAllowedError") {
            setError("Camera & mic blocked. In your browser settings, allow this site to use camera + microphone and try again.");
          } else if (err2?.name === "NotFoundError") {
            setError("No camera/mic detected on this device.");
          } else if (err2?.name === "NotReadableError") {
            setError("Your camera is already in use by another app. Close it and try again.");
          } else {
            setError(`Couldn't start video: ${err2?.message || err2?.name || "unknown error"}`);
          }
          return;
        }
      }
      if (!active) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      localStreamRef.current = stream;
      setLocalStream(stream);
      send({ type: "video-join" });
      joinedRef.current = true;
    })();

    const unregister = registerHandler(async (data) => {
      if (data.type === "video-peers") {
        // I just joined — existing peers are data.peers; I initiate offers to each.
        for (const pid of data.peers || []) {
          const info = (members || []).find((m) => m.id === pid);
          const pc = createPeer(pid, info);
          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            send({ type: "rtc-offer", to_user_id: pid, payload: offer });
          } catch (e) {
            console.error("offer failed", e);
          }
        }
      } else if (data.type === "video-join") {
        // A new peer joined after me. They will send me an offer; I don't initiate.
        // Pre-create the connection so addTrack happens before negotiation.
        createPeer(data.user_id, { username: data.username, avatar_gradient: data.avatar_gradient });
      } else if (data.type === "video-leave") {
        cleanupPeer(data.user_id);
      } else if (data.type === "rtc-offer") {
        const fromId = data.from_user_id;
        const info = (members || []).find((m) => m.id === fromId) || { username: data.from_username };
        const pc = createPeer(fromId, info);
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(data.payload));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          send({ type: "rtc-answer", to_user_id: fromId, payload: answer });
        } catch (e) {
          console.error("answer failed", e);
        }
      } else if (data.type === "rtc-answer") {
        const pc = pcsRef.current[data.from_user_id];
        if (pc) {
          try { await pc.setRemoteDescription(new RTCSessionDescription(data.payload)); } catch (e) { console.error(e); }
        }
      } else if (data.type === "rtc-ice") {
        const pc = pcsRef.current[data.from_user_id];
        if (pc && data.payload) {
          try { await pc.addIceCandidate(new RTCIceCandidate(data.payload)); } catch (e) { /* ignore benign */ }
        }
      }
    });

    return () => {
      active = false;
      unregister?.();
      // Only send video-leave if we actually joined
      if (joinedRef.current) {
        send({ type: "video-leave" });
        joinedRef.current = false;
      }
      // Close all peer connections
      Object.keys(pcsRef.current).forEach((pid) => {
        try { pcsRef.current[pid].close(); } catch {}
      });
      pcsRef.current = {};
      // Stop local media
      const s = localStreamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMic = () => {
    const next = !micOn;
    setMicOn(next);
    localStreamRef.current?.getAudioTracks().forEach((t) => (t.enabled = next));
  };

  const toggleCam = () => {
    const next = !camOn;
    setCamOn(next);
    localStreamRef.current?.getVideoTracks().forEach((t) => (t.enabled = next));
  };

  // Tiles: local + remotes
  const tiles = [
    { id: me.id, isLocal: true, stream: localStream, user: me },
    ...Object.entries(remotes).map(([id, r]) => ({
      id,
      isLocal: false,
      stream: r.stream,
      user: { id, username: r.username, avatar_gradient: r.avatar_gradient },
    })),
  ];

  // Responsive grid: 1 tile -> full, 2 tiles -> 1 col on mobile / 2 on desktop,
  // 3-4 tiles -> 2 cols, larger screens -> 2x2. Mobile defaults to single column
  // so each face is big enough to read.
  let gridCols;
  if (tiles.length <= 1) gridCols = "grid-cols-1";
  else if (tiles.length === 2) gridCols = "grid-cols-1 sm:grid-cols-2";
  else gridCols = "grid-cols-1 sm:grid-cols-2";

  return (
    <div className="fixed inset-0 z-50 bg-[#050505]/95 backdrop-blur-md flex flex-col" data-testid="video-call-overlay">
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-white/5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-400 font-bold">Live · video</div>
          <div className="font-display text-base sm:text-lg font-bold">{tiles.length} on camera · max 4</div>
        </div>
        <button
          onClick={onLeave}
          data-testid="end-video-call-btn"
          className="ghost-btn inline-flex items-center gap-2 border-rose-500/30 text-rose-400 hover:bg-rose-500/10 px-4 py-2 sm:px-6 sm:py-3 text-sm"
        >
          <PhoneOff className="w-4 h-4" /> End
        </button>
      </div>

      {error && (
        <div className="mx-4 sm:mx-6 mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-300 p-3 text-sm">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3 sm:p-4">
        <div className={`grid ${gridCols} gap-3 sm:gap-4 h-full auto-rows-fr`}>
          {tiles.map((t) => (
            <Tile key={t.id} tile={t} camOn={t.isLocal ? camOn : true} micOn={t.isLocal ? micOn : true} />
          ))}
        </div>
      </div>

      <div
        className="flex items-center justify-center gap-3 py-5 border-t border-white/5 bg-[#0a0a0c]"
        style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <button
          onClick={toggleMic}
          data-testid="toggle-mic-btn"
          className={`w-12 h-12 rounded-full flex items-center justify-center transition ${micOn ? "bg-white/5 hover:bg-white/10 text-white" : "bg-rose-500/20 text-rose-400 hover:bg-rose-500/30"}`}
          aria-label={micOn ? "Mute mic" : "Unmute mic"}
        >
          {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </button>
        <button
          onClick={toggleCam}
          data-testid="toggle-cam-btn"
          className={`w-12 h-12 rounded-full flex items-center justify-center transition ${camOn ? "bg-white/5 hover:bg-white/10 text-white" : "bg-rose-500/20 text-rose-400 hover:bg-rose-500/30"}`}
          aria-label={camOn ? "Turn camera off" : "Turn camera on"}
        >
          {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </button>
        <button
          onClick={onLeave}
          className="w-14 h-12 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-[0_0_24px_rgba(244,63,94,0.4)]"
          aria-label="End call"
        >
          <PhoneOff className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function Tile({ tile, camOn, micOn }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !tile.stream) return;
    el.srcObject = tile.stream;
    // iOS Safari + some Android browsers need an explicit play() call after
    // srcObject is set. Catch the error silently — an unplayable stream
    // still falls through to the avatar placeholder below.
    const p = el.play?.();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [tile.stream]);

  const hasVideoTrack = tile.stream && tile.stream.getVideoTracks().some((t) => t.enabled);

  return (
    <div className="relative rounded-3xl overflow-hidden bg-[#0a0a0c] border border-white/5">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={tile.isLocal}
        className={`w-full h-full object-cover ${tile.isLocal ? "scale-x-[-1]" : ""}`}
        data-testid={`video-tile-${tile.id}`}
      />
      {!hasVideoTrack && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-black to-[#1a1a20]">
          <Avatar user={tile.user} size={96} />
        </div>
      )}
      <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-black/60 backdrop-blur-md rounded-full pl-1 pr-3 py-1 border border-white/5">
        <Avatar user={tile.user} size={24} />
        <span className="text-xs font-semibold">
          {tile.isLocal ? `${tile.user.username} (you)` : tile.user.username}
        </span>
        {!micOn && <MicOff className="w-3 h-3 text-rose-400" />}
      </div>
    </div>
  );
}
